import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogConfig } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import {
  DialogActionsDirective,
  DialogComponent,
  DialogContentDirective,
  DialogHeroDirective,
  DialogTitleDirective,
} from '@shared/components/dialog/dialog.component';
import { ResultDialogChoice, ResultOutcome, ResultReport, ResultTone } from './result-report';

/** Wide enough for a list of people beside their outcomes; a message alone reads better narrow. */
export function resultDialogConfig(report: ResultReport): MatDialogConfig {
  return {
    width: report.sections?.length ? '560px' : '400px',
    maxWidth: 'calc(100vw - 32px)',
    autoFocus: 'dialog',
  };
}

const TONE_ICONS: Record<ResultTone, string> = {
  [ResultTone.Success]: 'check_circle',
  [ResultTone.Partial]: 'warning',
  [ResultTone.Refused]: 'block',
  [ResultTone.Failed]: 'error',
  [ResultTone.Info]: 'info',
};

const OUTCOME_MODIFIERS: Record<ResultOutcome, string> = {
  [ResultOutcome.Added]: 'added',
  [ResultOutcome.Expanded]: 'expanded',
  [ResultOutcome.Unchanged]: 'unchanged',
  [ResultOutcome.NeedsFix]: 'needs-fix',
  [ResultOutcome.Failed]: 'failed',
};

@Component({
  selector: 'app-result-dialog',
  templateUrl: './result-dialog.component.html',
  styleUrl: './result-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButton,
    MatDialogClose,
    MatIcon,
    TranslatePipe,
    DialogComponent,
    DialogHeroDirective,
    DialogTitleDirective,
    DialogContentDirective,
    DialogActionsDirective,
  ],
})
export class ResultDialogComponent {
  protected readonly report = inject<ResultReport>(MAT_DIALOG_DATA);
  protected readonly toneIcon = TONE_ICONS[this.report.tone];
  protected readonly outcomeModifiers = OUTCOME_MODIFIERS;
  protected readonly secondaryChoice: ResultDialogChoice = 'secondary';

  /** A count beside a lone section, or a lone row, only repeats what is under it. */
  protected readonly showCounts = (this.report.sections?.length ?? 0) > 1;

  protected readonly closeKey = this.report.primaryAction?.key ?? closeKeyFor(this.report.tone);
}

/** Something went through, so the task is over; a refusal is only dismissed; a notice is read. */
function closeKeyFor(tone: ResultTone): string {
  switch (tone) {
    case ResultTone.Success:
    case ResultTone.Partial:
      return 'SHARED.SYSTEM.DONE';
    case ResultTone.Info:
      return 'SHARED.NOTIFICATIONS.SNACKBAR_OK';
    default:
      return 'SHARED.NOTIFICATIONS.ERROR_DIALOG_CLOSE';
  }
}
